import React, { useState, useEffect } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Button, InputNumber, Tabs, Input as AntDInput, Alert, Space, message, Row, Col } from 'antd';
import DefaultLayout from 'components/DefaultLayout';
import Spinner from 'components/Spinner';
import apiClient from 'utils/apiClient';
import { config } from 'config/config';
import { Input, RemoveConfirmSelect, Select } from 'components/Form';
import Variables from '../Variables';
import LanguageRadioButtons from 'components/LanguageRadioButtons';
import { TextEditor } from 'ui';
import './styles.scss';
import { useT } from 'hooks/Locale/useT';

const mapStateToProps = (state: any) => {
    return {
        campaignData: state.campaignData,
        session: state.session,
    };
};

const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 },
};

const TemplateForm: React.FC<any> = ({
    session,
    submitForm,
    form,
    isLoading,
    setMailableArray,
    supportedLanguages,
    setSupportedLanguages,
    template,
    htmlTemplate,
    setHtmlTemplate,
}) => {
    const t = useT();
    const [mailable, setMailable] = useState<string | undefined>();
    const [selectLanguage, setSelectLanguage] = useState<string>('');
    const [languageList, setLanguageList] = useState({});
    const [editor, setEditor] = useState<any>();
    const [inputType, setInputType] = useState<'htmlTemplate' | 'smsTemplate'>('htmlTemplate');
    const [mailableOptions, setMailableOptions] = useState<Record<string, string>>({});

    const params = useParams();
    const templateId = params.id;
    const { TextArea } = AntDInput;

    const intl = useIntl();

    useEffect(() => {
        loadLanguages();

        // While editing, the template's own category decides the list, so the unfiltered
        // request would only race with it.
        if (!templateId) {
            loadMailable();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (template) {
            setSelectLanguage(template.language);
            form.setFieldsValue({ language: template.language });
        }
    }, [template]);

    useEffect(() => {
        if (!template) return;

        const currentMailable = template.mailable
            ? template.mailable.split('\\').pop()
            : undefined;

        setMailable(currentMailable);
        form.setFieldsValue({ mailable: currentMailable ?? null });

        loadMailable(template.categoryId, currentMailable);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template]);

    /**
     * Mailables are offered per category by the API. The template's own mailable is kept in
     * the list even when the category no longer offers it, so the template stays editable.
     */
    const loadMailable = async (categoryId?: number, keepMailable?: string) => {
        const response = await apiClient.request(
            '/api/v1/mail-templates/options/standard-mailable',
            categoryId ? { categoryId } : {},
            'GET'
        );

        if (!categoryId) {
            setMailableArray(response);
        }

        const options: Record<string, string> = {};
        [...response, ...(keepMailable ? [keepMailable] : [])].forEach((name: string) => {
            options[name] = name;
        });

        setMailableOptions(options);
    };

    const loadLanguages = async () => {
        let languages = await apiClient.request(config.api.routes.backend.languages, {}, 'GET');
        let languageListForSelect: any = {};
        Object.values(languages.languages).map((value: any) => {
            return (languageListForSelect[value['code']] = value['name']);
        });
        setLanguageList(languageListForSelect);
    };

    const deleteLangugae = async (language: string) => {
        const newSupportedLanguages = supportedLanguages.filter((element: string) => {
            return element !== language;
        });
        setSupportedLanguages(newSupportedLanguages);
    };

    const languageChange = (languageCode: string) => {
        if (languageCode === selectLanguage) {
            return;
        }

        if (!templateId) {
            message.info(intl.formatMessage({ id: 'emailing.save_template_info' }));
            return;
        }

        const values = form.getFieldsValue();
        submitForm(values, languageCode);

        setSelectLanguage(languageCode);
    };

    const handleChange = (languageArray: any) => {
        if (!supportedLanguages.length) {
            form.setFieldsValue({
                language: languageArray[languageArray.length - 1],
            });
        }
    };
    const handleCategoryChange = (categoryId: number) => {
        setMailable(undefined);
        form.setFieldsValue({ mailable: null });
        loadMailable(categoryId);
    };

    return (
        <DefaultLayout.PageLayout withStickyFooter>
            <DefaultLayout.PageHeader
                breadcrumb={[
                    {
                        name: intl.formatMessage({ id: 'emailing.templates.manage_templates' }),
                        path: '/e-mailing/manage-templates',
                    },
                ]}
                title={intl.formatMessage({
                    id: templateId
                        ? 'emailing.templates.edit_template'
                        : 'emailing.templates.add_template',
                })}
            />
            <Spinner spinning={isLoading} opaque={isLoading}>
                <DefaultLayout.PageContent withTopPadding>
                    <Form form={form} onFinish={submitForm} {...formItemLayout}>
                        <Input
                            customLayout={true}
                            name="title"
                            label={intl.formatMessage({ id: 'emailing.templates.template_name' })}
                            validation={{ required: true }}
                        />
                        <Select
                            name="categoryId"
                            url="/api/v1/mail-templates/options/categories"
                            customLayout={formItemLayout}
                            label={intl.formatMessage({ id: 'general.category' })}
                            integerKey={true}
                            customRules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({
                                        id: 'validation.field_required',
                                    }),
                                },
                            ]}
                            customObjLabel={(label: string) =>
                                // Falls back to the raw name: not every category has a label yet.
                                t(`email_templates.categories.${label}`, label)
                            }
                            onChange={(el: number) => handleCategoryChange(el)}
                        />
                        <Select
                            name="mailable"
                            customLayout={formItemLayout}
                            manualOptions={mailableOptions}
                            label={intl.formatMessage({ id: 'emailing.templates.mailable' })}
                            labelKey={true}
                            customRules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({
                                        id: 'validation.field_required',
                                    }),
                                },
                            ]}
                            customObjLabel={(label: string) =>
                                // Falls back to the raw name: not every mailable has a label yet.
                                t(`email_templates.mailables.${label}`, label)
                            }
                            onChange={(id: any) => setMailable(id)}
                        />
                        <Input
                            customLayout={true}
                            name="from"
                            label={intl.formatMessage({ id: 'general.from' })}
                            disabled={!session.access.appearance.allow}
                        />
                        <Input
                            customLayout={true}
                            name="subject"
                            label={intl.formatMessage({ id: 'emailing.templates.subject' })}
                            validation={{ required: true }}
                        />
                        <RemoveConfirmSelect
                            name="supportedLanguages"
                            label={intl.formatMessage({ id: 'emailing.supported_languages' })}
                            valueKey="value"
                            labelKey="label"
                            customLayout={true}
                            manualOptions={languageList}
                            customRules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({
                                        id: 'validation.field_required_for_language',
                                    }),
                                },
                            ]}
                            selectedOptions={supportedLanguages}
                            setSelectedOptions={setSupportedLanguages}
                            onChange={(el: any) => handleChange(el)}
                            removeFunction={deleteLangugae}
                            confirmModalOK={intl.formatMessage({ id: 'general.delete' })}
                            confirmModalTitle={intl.formatMessage({
                                id: 'emailing.delete_this_template_language',
                            })}
                            confirmModalCancel={intl.formatMessage({ id: 'general.cancel' })}
                        />
                        <LanguageRadioButtons
                            languages={supportedLanguages}
                            onClickFunction={languageChange}
                        />

                        <Variables
                            editor={editor}
                            mailable={mailable ?? ''}
                            form={form}
                            inputType={inputType}
                        />
                        <Form.Item label="‎" className="form-alert noColon">
                            <Alert
                                message={intl.formatMessage({
                                    id: 'emailing.variable_info_message',
                                })}
                                type="info"
                            />
                        </Form.Item>

                        <Row>
                            <Col span={formItemLayout.labelCol.span}>
                                <label className="flex justify-end mr-2 mt-3">
                                    {t('emailing.templates.html_template')} :
                                </label>
                            </Col>
                            <Col span={formItemLayout.wrapperCol.span}>
                                <Tabs
                                    onChange={(tabKey: string) =>
                                        setInputType(tabKey as 'htmlTemplate' | 'smsTemplate')
                                    }
                                >
                                    <Tabs.TabPane
                                        key="htmlTemplate"
                                        tab={t('emailing.templates.html_template')}
                                    >
                                        <TextEditor
                                            key={`editor-${templateId}`}
                                            initial={htmlTemplate}
                                            setHtmlOutput={setHtmlTemplate}
                                            onEditorReady={(editorInstance) => {
                                                setEditor(editorInstance);
                                            }}
                                        />
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        key="smsTemplate"
                                        tab={t('emailing.templates.sms_template')}
                                    >
                                        <Form.Item
                                            className="sms-text-area"
                                            name="smsTemplate"
                                            colon
                                        >
                                            <TextArea
                                                showCount
                                                maxLength={160}
                                            />
                                        </Form.Item>
                                    </Tabs.TabPane>
                                </Tabs>
                            </Col>
                        </Row>
                    </Form>
                </DefaultLayout.PageContent>
            </Spinner>
            <DefaultLayout.PageFooter
                right={
                    <Space size={10}>
                        <NavLink to="/e-mailing/manage-templates">
                            <Button>
                                <FormattedMessage id="general.back" />
                            </Button>
                        </NavLink>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isLoading}
                            onClick={() => form.submit()}
                        >
                            <FormattedMessage id="general.submit" />
                        </Button>
                    </Space>
                }
            />
        </DefaultLayout.PageLayout>
    );
};

export default connect(mapStateToProps)(TemplateForm);
